import { Grid, type GridProps, type GridSize, type GridSpacing } from '../primitives/index.js'
import { Typography } from '../primitives/index.js'
import { graphHooks } from '@tunasong/graph-lib/react'
import { getImageUrl } from '@tunasong/models'
import { getPluginByType, usePlugins } from '@tunasong/plugin-lib'
import type { Entity, Persisted } from '@tunasong/schemas'
import { isImage } from '@tunasong/schemas'
import cn from 'classnames'
import range from 'just-range'
import type { FC } from 'react'
import { useCallback } from 'react'
import { useDimensions } from '../layout/dimensions.js'
import { EntityCard } from './entity-card.js'
import styles from './entity-grid.module.css'

export interface EntityGridProps extends Omit<GridProps, 'size'> {
  className?: string
  size?: 'small' | 'medium' | 'large' | 'tiny'
  entities: Persisted<Entity>[]
  skeletonItems?: number
  placeholder?: string
  loading?: boolean
  // Show the action button in the item header
  showItemHeaderAction?: boolean
  showItemHeader?: boolean
  onOpen?(entity: Persisted<Entity>): void
}

export const EntityGrid: FC<EntityGridProps> = props => {
  const {
    onOpen,
    placeholder = 'No entities found',
    skeletonItems: providedSkeletonItems,
    showItemHeaderAction = false,
    entities = [],
    loading = false,
    size = 'medium',
    className,
    ...restProps
  } = props
  const plugins = usePlugins('all')
  const { isSmallOrSmaller } = useDimensions()

  const handleOpen = useCallback(
    (entity: Persisted<Entity>) => () => {
      if (!onOpen) {
        return
      }
      onOpen(entity)
    },
    [onOpen]
  )
  const { spacing, xs, sm, md, lg }: { spacing: GridSpacing; sm: GridSize; md: GridSize; lg: GridSize; xs: GridSize } =
    {
      spacing: 1,
      xs: 12,
      sm: 6,
      md: 4,
      lg: 4,
    }
  const { isTiny } = useDimensions()

  const skeletonItems = providedSkeletonItems ? providedSkeletonItems : isTiny ? 2 : 3

  const { data: imageEntities } = graphHooks.useEntitiesByEdgeByIds({
    sourceIds: entities.map(e => e.id),
    relation: 'hasImage',
  })

  /** We want to get all the entityIds hasImage relation */

  if (entities.length === 0 && !loading) {
    return (
      <div className={styles.emptyState}>
        <Typography variant="caption" className={styles.emptyText}>
          {placeholder}
        </Typography>
      </div>
    )
  }

  return (
    <Grid className={cn(styles.gridContainer, className)} spacing={spacing} container={true} {...restProps}>
      {/* Skeleton cards when loading  */}

      {loading
        ? range(skeletonItems).map(n => (
            <Grid size={{ xs, sm, md, lg }} key={n}>
              <EntityCard key={n} />
            </Grid>
          ))
        : entities.map((e, idx) => {
            const plugin = getPluginByType(e.type, plugins)
            const Card = plugin?.components?.Card ?? EntityCard
            const key = e.id ?? idx

            const image = imageEntities?.find(i => i.parentId === e.id)
            const imageUrl = isImage(image) ? getImageUrl(image, 'small') : undefined
            return (
              <Grid size={{ xs, sm, md, lg }} key={key}>
                <Card
                  entity={e}
                  size={size}
                  backgroundImage={imageUrl}
                  onClick={handleOpen(e)}
                  showSecondary={!isSmallOrSmaller}
                  aria-label={`Entity Card ${e.name}`}
                  headerAction={showItemHeaderAction ? undefined : null}
                />
              </Grid>
            )
          })}
    </Grid>
  )
}

export default EntityGrid
